import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Loader, LogIn, ShieldAlert, LogOut } from 'lucide-react';
import { attendanceService, clubService } from '../services/supabaseService';
import { coachServiceProxy, coachClubAccessServiceProxy, storageServiceProxy } from '../services/proxyService';
import db from '../services/dbProxy';
import { useAuth } from '../context/AuthContext';

const CoachQRAttendance = () => {
  const { clubId, token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, profile, loading: authLoading } = useAuth();

  // loading | ready_in | ready_out | success_in | success_out | error | no_session | not_approved | complete
  const [status, setStatus] = useState('loading');
  const [coachName, setCoachName] = useState('');
  const [clubName, setClubName] = useState('');
  const [clubLogoUrl, setClubLogoUrl] = useState(null);
  const [marking, setMarking] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [checkInTime, setCheckInTime] = useState(null);
  const [coachId, setCoachId] = useState(null);
  const [coachPhotoUrl, setCoachPhotoUrl] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    validateAndLoad();
    // eslint-disable-next-line
  }, [authLoading]);

  const validateAndLoad = async () => {
    try {
      // 1. Validate coach session via Supabase Auth
      if (!isAuthenticated || !profile?.id) {
        setStatus('no_session');
        localStorage.setItem('qr_redirect', window.location.pathname);
        return;
      }

      const sessionCoachId = profile.id;
      setCoachId(sessionCoachId);

      // 2. Validate QR token belongs to this club
      const { data: clubToken, error: tokenErr } = await clubService.getByQrToken(clubId, token);
      if (tokenErr || !clubToken) {
        setStatus('error');
        setErrorMsg('QR inválido o expirado. Solicita al club un QR actualizado.');
        return;
      }

      // 3. Coach exists
      const { data: coach, error: coachErr } = await coachServiceProxy.getById(sessionCoachId);
      if (coachErr || !coach) { setStatus('error'); setErrorMsg('Entrenador no encontrado'); return; }
      setCoachName(coach.name);

      // Load coach photo
      try {
        const { data: photoUrl } = await storageServiceProxy.getCoachPhoto(sessionCoachId);
        if (photoUrl) setCoachPhotoUrl(photoUrl);
      } catch (e) { /* photo optional */ }

      // 4. Approved for this club
      const { data: access } = await coachClubAccessServiceProxy.getByCoachAndClub(sessionCoachId, clubId);
      if (!access || access.status !== 'approved') {
        setStatus('not_approved');
        setErrorMsg('No estás aprobado como entrenador en este club. Solicita acceso primero.');
        return;
      }

      // 5. Club info + logo
      const { data: club, error: clubErr } = await clubService.getById(clubId);
      if (clubErr || !club) { setStatus('error'); setErrorMsg('Club no encontrado'); return; }
      setClubName(club.name);

      // Load club logo
      try {
        const { data: logoUrl } = await storageServiceProxy.getClubLogo(clubId);
        if (logoUrl) setClubLogoUrl(logoUrl);
      } catch (e) { /* logo optional */ }

      // 6. Check existing record today
      const today = (() => {
        const n = new Date();
        return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
      })();
      console.log('[QR] Checking existing attendance for date:', today, 'coach:', sessionCoachId);
      const { data: existing } = await db('coach_attendance')
        .select('id, status, check_in_time, check_out_time')
        .eq('coach_id', sessionCoachId).eq('club_id', clubId).eq('date', today);

      const record = existing && existing.length > 0 ? existing[0] : null;

      if (record && record.status === 'present') {
        if (record.check_out_time) {
          setCheckInTime(record.check_in_time);
          setStatus('complete');
        } else {
          setCheckInTime(record.check_in_time);
          setStatus('ready_out');
        }
      } else {
        setStatus('ready_in');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message);
    }
  };

  // Helper: fecha local YYYY-MM-DD (no depende de UTC)
  const getLocalDate = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  const handleCheckIn = async () => {
    setMarking(true);
    try {
      const today = getLocalDate();
      const now = new Date().toISOString();
      console.log('[QR] Check-in:', { coachId, clubId, date: today, time: now });

      // Intentar upsert primero
      const result = await attendanceService.markCoachAttendance([{
        coach_id: coachId, club_id: clubId, date: today,
        status: 'present', check_in_time: now
      }]);

      if (result.error) {
        // Si falla upsert, intentar insert directo
        console.warn('[QR] Upsert failed, trying direct insert:', result.error);
        const { error: insertErr } = await db('coach_attendance')
          .insert({
            coach_id: coachId, club_id: clubId, date: today,
            status: 'present', check_in_time: now
          });
        if (insertErr) {
          // Si insert falla por duplicado, intentar update
          console.warn('[QR] Insert failed, trying update:', insertErr.message);
          const { error: updateErr } = await db('coach_attendance')
            .update({ status: 'present', check_in_time: now })
            .eq('coach_id', coachId)
            .eq('date', today);
          if (updateErr) throw new Error(updateErr.message);
        }
      }

      console.log('[QR] Check-in exitoso');
      setStatus('success_in');
    } catch (err) {
      console.error('[QR] Error check-in:', err);
      setStatus('error'); setErrorMsg(err.message);
    } finally { setMarking(false); }
  };

  const handleCheckOut = async () => {
    setMarking(true);
    try {
      const today = getLocalDate();
      const now = new Date().toISOString();
      console.log('[QR] Check-out:', { coachId, clubId, date: today, time: now });

      const result = await attendanceService.markCoachCheckOut(coachId, clubId, today);
      if (result.error) {
        // Fallback: update directo
        console.warn('[QR] CheckOut service failed, trying direct update:', result.error);
        const { error: updateErr } = await db('coach_attendance')
          .update({ check_out_time: now })
          .eq('coach_id', coachId)
          .eq('club_id', clubId)
          .eq('date', today);
        if (updateErr) throw new Error(updateErr.message);
      }

      console.log('[QR] Check-out exitoso');
      setStatus('success_out');
    } catch (err) {
      console.error('[QR] Error check-out:', err);
      setStatus('error'); setErrorMsg(err.message);
    } finally { setMarking(false); }
  };

  const handleGoToLogin = () => {
    localStorage.setItem('qr_redirect', window.location.pathname);
    navigate('/entrenadores');
  };

  const now = new Date();
  const timeStr = now.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
  const dateStr = now.toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const formatTime = (iso) => iso ? new Date(iso).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }) : '';

  const btnStyle = (bg, disabled) => ({
    width: '100%', padding: '16px 24px', borderRadius: 14,
    background: disabled ? '#a78bfa' : bg,
    color: 'white', border: 'none', fontWeight: 700, fontSize: '1rem',
    cursor: disabled ? 'wait' : 'pointer', fontFamily: 'inherit',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(139,92,246,0.3)'
  });

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #ddd6fe 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, fontFamily: "'Inter', -apple-system, sans-serif"
    }}>
      <div style={{
        background: 'white', borderRadius: 20, padding: '40px 32px',
        maxWidth: 400, width: '100%', textAlign: 'center',
        boxShadow: '0 20px 60px rgba(139,92,246,0.15)',
        border: '1px solid #e9e5f5'
      }}>
        {/* Club Logo */}
        {clubLogoUrl ? (
          <img
            src={clubLogoUrl}
            alt={clubName}
            style={{
              width: 64, height: 64, borderRadius: 16,
              objectFit: 'cover', border: '2px solid #e9e5f5',
              margin: '0 auto 16px', display: 'block'
            }}
          />
        ) : (
          <div style={{
            width: 64, height: 64, borderRadius: 16,
            background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', fontSize: 22, color: 'white', fontWeight: 700
          }}>
            {(clubName || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
          </div>
        )}

        <h2 style={{ margin: '0 0 4px', fontSize: '1.1rem', color: '#18181b' }}>
          {clubName || 'Cargando...'}
        </h2>
        <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '0.82rem' }}>
          Control de Asistencia — Entrenadores
        </p>

        <div style={{
          background: '#faf9fe', borderRadius: 12, padding: '12px 16px',
          marginBottom: 24, border: '1px solid #f1f0f9'
        }}>
          <p style={{ margin: '0 0 2px', fontSize: '0.78rem', color: '#8b5cf6', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {dateStr}
          </p>
          <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#18181b' }}>
            <Clock size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            {timeStr}
          </p>
        </div>

        {status === 'loading' && (
          <div style={{ padding: 20 }}>
            <Loader size={32} style={{ animation: 'spin 1s linear infinite', color: '#1a1a1a' }} />
            <p style={{ color: '#64748b', marginTop: 12, fontSize: '0.88rem' }}>Validando sesión...</p>
          </div>
        )}

        {/* No session */}
        {status === 'no_session' && (
          <div style={{ padding: 10 }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <LogIn size={32} style={{ color: '#f59e0b' }} />
            </div>
            <h3 style={{ margin: '0 0 6px', color: '#92400e', fontSize: '1.05rem' }}>Inicia sesión primero</h3>
            <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '0.85rem' }}>
              Debes iniciar sesión como entrenador para marcar asistencia. Solo necesitas hacerlo una vez.
            </p>
            <button onClick={handleGoToLogin} style={btnStyle('linear-gradient(135deg, #8b5cf6, #7c3aed)', false)}>
              <LogIn size={18} /> Ir a Iniciar Sesión
            </button>
          </div>
        )}

        {/* Not approved for this club */}
        {status === 'not_approved' && (
          <div style={{ padding: 10 }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <ShieldAlert size={32} style={{ color: '#dc2626' }} />
            </div>
            <h3 style={{ margin: '0 0 6px', color: '#991b1b', fontSize: '1.05rem' }}>Sin acceso al club</h3>
            <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem' }}>{errorMsg}</p>
          </div>
        )}

        {/* Ready for CHECK-IN */}
        {status === 'ready_in' && (
          <>
            <div style={{ background: '#f1f5f9', borderRadius: 12, padding: '14px 16px', marginBottom: 20, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 12 }}>
              {coachPhotoUrl ? (
                <img src={coachPhotoUrl} alt={coachName} style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: '1rem' }}>
                  {coachName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                </div>
              )}
              <div>
                <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>Entrenador</p>
                <p style={{ margin: '2px 0 0', fontSize: '1.05rem', fontWeight: 700, color: '#18181b' }}>{coachName}</p>
              </div>
            </div>
            <button onClick={handleCheckIn} disabled={marking} style={btnStyle('linear-gradient(135deg, #10b981, #059669)', marking)}>
              {marking
                ? <><Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /> Registrando...</>
                : <><CheckCircle size={20} /> Marcar Entrada</>
              }
            </button>
          </>
        )}

        {/* Ready for CHECK-OUT (already checked in) */}
        {status === 'ready_out' && (
          <>
            <div style={{ background: '#f0fdf4', borderRadius: 12, padding: '14px 16px', marginBottom: 12, border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: 12 }}>
              {coachPhotoUrl ? (
                <img src={coachPhotoUrl} alt={coachName} style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: '1rem' }}>
                  {coachName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                </div>
              )}
              <div>
                <p style={{ margin: 0, fontSize: '0.82rem', color: '#166534' }}>✅ Entrada registrada</p>
                <p style={{ margin: '2px 0 0', fontSize: '1rem', fontWeight: 700, color: '#18181b' }}>
                  {coachName} — {checkInTime ? formatTime(checkInTime) : ''}
                </p>
              </div>
            </div>
            <button onClick={handleCheckOut} disabled={marking} style={btnStyle('linear-gradient(135deg, #f59e0b, #d97706)', marking)}>
              {marking
                ? <><Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /> Registrando...</>
                : <><LogOut size={20} /> Marcar Salida</>
              }
            </button>
          </>
        )}

        {/* Success CHECK-IN */}
        {status === 'success_in' && (
          <div style={{ padding: 10 }}>
            {coachPhotoUrl ? (
              <img src={coachPhotoUrl} alt={coachName} style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', margin: '0 auto 16px' }} />
            ) : (
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <CheckCircle size={32} style={{ color: '#16a34a' }} />
              </div>
            )}
            <h3 style={{ margin: '0 0 4px', color: '#166534', fontSize: '1.1rem' }}>¡Entrada Registrada!</h3>
            <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem' }}>{coachName} — {timeStr}</p>
            <p style={{ margin: '12px 0 0', color: '#94a3b8', fontSize: '0.78rem' }}>Escanea el QR de nuevo al salir para registrar tu salida</p>
          </div>
        )}

        {/* Success CHECK-OUT */}
        {status === 'success_out' && (
          <div style={{ padding: 10 }}>
            {coachPhotoUrl ? (
              <img src={coachPhotoUrl} alt={coachName} style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', margin: '0 auto 16px' }} />
            ) : (
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <LogOut size={32} style={{ color: '#f59e0b' }} />
              </div>
            )}
            <h3 style={{ margin: '0 0 4px', color: '#92400e', fontSize: '1.1rem' }}>¡Salida Registrada!</h3>
            <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem' }}>{coachName} — {timeStr}</p>
          </div>
        )}

        {/* Complete — both in and out done */}
        {status === 'complete' && (
          <div style={{ padding: 10 }}>
            {coachPhotoUrl ? (
              <img src={coachPhotoUrl} alt={coachName} style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', margin: '0 auto 16px' }} />
            ) : (
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <CheckCircle size={32} style={{ color: '#64748b' }} />
              </div>
            )}
            <h3 style={{ margin: '0 0 4px', color: '#3f3f46', fontSize: '1.05rem' }}>Jornada completa</h3>
            <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem' }}>{coachName} ya registró entrada y salida hoy</p>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div style={{ padding: 10 }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <XCircle size={32} style={{ color: '#dc2626' }} />
            </div>
            <h3 style={{ margin: '0 0 4px', color: '#991b1b', fontSize: '1.1rem' }}>Error</h3>
            <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem' }}>{errorMsg}</p>
          </div>
        )}

        <p style={{ marginTop: 24, fontSize: '0.72rem', color: '#a1a1aa' }}>
          CopaGimnasta · Control de Asistencia
        </p>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CoachQRAttendance;
